import type { PHDocument } from "@powerhousedao/shared/document-model";
import { use, useCallback, useSyncExternalStore } from "react";
import { readPromiseState } from "../document-cache.js";
import type { IDocumentCache } from "../types/documents.js";
import type { SetPHGlobalValue, UsePHGlobalValue } from "../types/global.js";
import { makePHEventFunctions } from "./make-ph-event-functions.js";

const documentEventFunctions = makePHEventFunctions("documentCache");

/** Returns all documents in the reactor. */
export const useDocumentCache: UsePHGlobalValue<IDocumentCache> =
  documentEventFunctions.useValue;

/** Sets all of the documents in the reactor. */
export const setDocumentCache: SetPHGlobalValue<IDocumentCache> =
  documentEventFunctions.setValue;

/** Adds an event handler for all of the documents in the reactor. */
export const addDocumentCacheEventHandler =
  documentEventFunctions.addEventHandler;

/**
 * Reads the state of a document promise and converts it to a query state object.
 * @param promise - The document promise to read
 * @returns An object containing the status, data, error, and isPending flag
 */
function getDocumentQueryState(promise: Promise<PHDocument>) {
  const state = readPromiseState(promise);
  switch (state.status) {
    case "pending":
      return {
        status: "pending",
        isPending: true,
        error: undefined,
        data: undefined,
      } as const;
    case "fulfilled":
      return {
        status: "success",
        isPending: false,
        error: undefined,
        data: state.value,
      } as const;
    case "rejected":
      return {
        status: "error",
        isPending: false,
        error: state.reason,
        data: undefined,
      } as const;
  }
}

/**
 * Retrieves a document from the reactor and subscribes to changes using React Suspense.
 * This hook will suspend rendering while the document is loading.
 * @param id - The document ID to retrieve, or null/undefined to skip retrieval
 * @returns The document if found, or undefined if id is null/undefined
 */
export function useDocument(id: string | null | undefined) {
  const documentCache = useDocumentCache();
  const document = useSyncExternalStore(
    (cb) => (id && documentCache ? documentCache.subscribe(id, cb) : () => {}),
    () => (id ? documentCache?.get(id) : undefined),
  );
  return document ? use(document) : undefined;
}

/**
 * Retrieves a document from the reactor and subscribes to changes without
 * suspending or throwing. Use this when the ID might be stale (e.g., a
 * previously selected node that has since been deleted) and the consumer
 * wants to render a placeholder instead of crashing.
 *
 * Unlike {@link useGetDocumentAsync}, this hook subscribes to cache
 * updates, so the returned state stays in sync with reactor changes.
 *
 * @param id - The document ID to retrieve, or null/undefined to skip retrieval
 * @returns An object containing:
 *   - status: "initial" | "pending" | "success" | "error"
 *   - data: The document if successfully loaded
 *   - isPending: Whether the document is currently loading
 *   - error: Any error that occurred during loading (e.g., document not found)
 *   - reload: Function to force a refetch, or undefined when no id is supplied
 */
export function useDocumentSafe(id: string | null | undefined) {
  const documentCache = useDocumentCache();
  const promise = useSyncExternalStore(
    (cb) => (id && documentCache ? documentCache.subscribe(id, cb) : () => {}),
    () => (id ? documentCache?.get(id) : undefined),
  );

  if (!promise || !documentCache || !id) {
    return {
      status: "initial",
      isPending: false,
      error: undefined,
      data: undefined,
      reload: undefined,
    } as const;
  }

  const state = getDocumentQueryState(promise);
  return { ...state, reload: () => documentCache.get(id, true) } as const;
}

/**
 * Retrieves multiple documents from the reactor using React Suspense.
 * This hook will suspend rendering while any of the documents are loading.
 *
 * Uses getBatch from the document cache which handles promise caching internally,
 * ensuring stable references for useSyncExternalStore.
 *
 * @param ids - Array of document IDs to retrieve, or null/undefined to skip retrieval
 * @returns An array of documents if found, or empty array if ids is null/undefined
 */
export function useDocuments(ids: string[] | null | undefined) {
  const documentCache = useDocumentCache();

  const documents = useSyncExternalStore(
    (cb) =>
      ids?.length && documentCache
        ? documentCache.subscribe(ids, cb)
        : () => {},
    () =>
      ids?.length && documentCache ? documentCache.getBatch(ids) : undefined,
  );

  return documents ? use(documents) : [];
}

/**
 * Returns a function to retrieve a document from the cache.
 * The returned function fetches and returns a document by ID.
 * @returns A function that takes a document ID and returns a Promise of the document
 */
export function useGetDocument() {
  const documentCache = useDocumentCache();

  return useCallback(
    (id: string) => {
      if (!documentCache) {
        return Promise.reject(new Error("Document cache not initialized"));
      }
      return documentCache.get(id);
    },
    [documentCache],
  );
}

/**
 * Returns a function to retrieve multiple documents from the cache.
 * The returned function fetches and returns documents by their IDs.
 * @returns A function that takes an array of document IDs and returns a Promise of the documents
 */
export function useGetDocuments() {
  const documentCache = useDocumentCache();

  return useCallback(
    (ids: string[]) => {
      if (!documentCache) {
        return Promise.reject(new Error("Document cache not initialized"));
      }
      return documentCache.getBatch(ids);
    },
    [documentCache],
  );
}

/**
 * Retrieves a document from the reactor without suspending rendering.
 * Returns the current state of the document loading operation.
 * @param id - The document ID to retrieve, or null/undefined to skip retrieval
 * @returns An object containing:
 *   - status: "initial" | "pending" | "success" | "error"
 *   - data: The document if successfully loaded
 *   - isPending: Boolean indicating if the document is currently loading
 *   - error: Any error that occurred during loading
 *   - reload: Function to force reload the document from cache
 */
export function useGetDocumentAsync(id: string | null | undefined) {
  const documentCache = useDocumentCache();
  if (!id || !documentCache) {
    return {
      status: "initial",
      data: undefined,
      isPending: false,
      error: undefined,
      reload: undefined,
    } as const;
  }

  const promise = documentCache.get(id);
  const state = getDocumentQueryState(promise);

  return { ...state, reload: () => documentCache.get(id, true) } as const;
}
